import { useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { EntityRefLink } from '@backstage/plugin-catalog-react';
import { BreadcrumbEntry, useRouteRef } from '@backstage/frontend-plugin-api';
import {
  Alert,
  Container,
  Flex,
  Header,
  Text,
  Grid,
  Tabs,
  TabList,
  Tab,
  TabPanel,
  Button,
} from '@backstage/ui';
import { RiCloseLine, RiCircleFill } from '@remixicon/react';
import {
  useTrackDetails,
  useGroupFilter,
  useTeamOwnerFilter,
  type TrackResultRow,
} from '../../hooks';
import {
  getDescendantOwnerRefs,
  toCanonicalOwnerRef,
} from '../insights/insights-group-scope';
import { FilterBar } from '../filter-bar';
import { PulseDetailsSkeleton } from '../details-skeleton';
import { EmptyState } from '../empty-state';
import {
  EMPTY_FILTERS,
  buildEntityOwnerSections,
  hasActiveEntitySelection,
  type Filters,
  type FilterOption,
} from '../filters';
import { TrackHistoryChartCard } from './track-history-chart-card';
import { CurrentStatusCard } from './current-status-card';
import { ChecksByLevels } from './checks-by-levels';
import { TrackEntitiesTable } from './track-entities-table';
import { getLevelImportanceLabel } from './track-utils';
import { TrackActionsButton } from './track-actions';
import { tracksDetailsSubRouteRef, tracksRouteRef } from '../../routes';
import { formatEntityFilter } from '../integrations/formatters';

const getResultOwnerGroup = (result: TrackResultRow): string | undefined => {
  const owner =
    typeof result.entity?.spec?.owner === 'string'
      ? result.entity.spec.owner
      : undefined;
  return owner ? toCanonicalOwnerRef(owner) : undefined;
};

const getResultKind = (result: TrackResultRow): string | undefined =>
  (result.entity?.kind ?? result.entityRef.split(':')[0])?.toLowerCase() ||
  undefined;

const getResultLifecycle = (result: TrackResultRow): string | undefined =>
  typeof result.entity?.spec?.lifecycle === 'string'
    ? result.entity.spec.lifecycle
    : undefined;

const toKindOptions = (results: TrackResultRow[]): FilterOption[] =>
  Array.from(
    new Set(
      results
        .map(getResultKind)
        .filter((kind): kind is string => kind !== undefined),
    ),
  )
    .sort((a, b) => a.localeCompare(b))
    .map(kind => ({
      value: kind,
      label: `${kind.charAt(0).toUpperCase()}${kind.slice(1)}`,
    }));

const toLifecycleOptions = (results: TrackResultRow[]): FilterOption[] =>
  Array.from(
    new Set(
      results
        .map(getResultLifecycle)
        .filter((lifecycle): lifecycle is string => lifecycle !== undefined),
    ),
  )
    .sort((a, b) => a.localeCompare(b))
    .map(lifecycle => ({ value: lifecycle, label: lifecycle }));

const TRACK_VIEWS = {
  CHECKS: 'checks',
  ENTITIES: 'entities',
} as const;

type TrackView = (typeof TRACK_VIEWS)[keyof typeof TRACK_VIEWS];

const withViewParam = (
  view: TrackView,
  currentLocation: ReturnType<typeof useLocation>,
) => {
  const nextParams = new URLSearchParams(currentLocation.search);
  nextParams.set('view', view);
  const query = nextParams.toString();
  return query
    ? `${currentLocation.pathname}?${query}`
    : currentLocation.pathname;
};

export const PulseTracksDetails = () => {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const getTracksPath = useRouteRef(tracksRouteRef);
  const getDetailsPath = useRouteRef(tracksDetailsSubRouteRef);
  const { value: details, track, loading, error } = useTrackDetails(id);
  const [savedNotice, setSavedNotice] = useState<'created' | 'updated' | null>(
    (location.state as { pulseTrackSaved?: 'created' | 'updated' } | null)
      ?.pulseTrackSaved ?? null,
  );
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const { selectedGroups, setSelectedGroups } = useGroupFilter();
  const { teamOptions, groupOptions, ownerByRef } = useTeamOwnerFilter();
  const breadcrumbLabel = details?.track.name ?? track?.name ?? id;
  const tracksPath = getTracksPath?.() ?? '..';
  const breadcrumbHref = getDetailsPath?.({ id }) ?? tracksPath;

  const results = useMemo(() => details?.results ?? [], [details]);

  const entityOwnerOptionsWithTeams = useMemo(
    () => buildEntityOwnerSections(teamOptions, groupOptions, selectedGroups),
    [teamOptions, groupOptions, selectedGroups],
  );
  const entityKindOptions = useMemo(() => toKindOptions(results), [results]);
  const lifecycleOptions = useMemo(
    () => toLifecycleOptions(results),
    [results],
  );

  const expandedSelectedGroups = useMemo(() => {
    if (selectedGroups.length === 0) return new Set<string>();
    const scoped = new Set<string>();
    for (const ref of selectedGroups) {
      scoped.add(ref);
      for (const descendant of getDescendantOwnerRefs(ref, ownerByRef)) {
        scoped.add(descendant);
      }
    }
    return scoped;
  }, [selectedGroups, ownerByRef]);

  const filteredResults = useMemo(() => {
    return results.filter(result => {
      const group = getResultOwnerGroup(result);
      const matchesGroup =
        expandedSelectedGroups.size === 0 ||
        (group ? expandedSelectedGroups.has(group) : false);

      const kind = getResultKind(result);
      const matchesKind =
        filters.entityKinds.length === 0 ||
        (kind ? filters.entityKinds.includes(kind) : false);

      const lifecycle = getResultLifecycle(result);
      const matchesLifecycle =
        filters.lifecycles.length === 0 ||
        (lifecycle ? filters.lifecycles.includes(lifecycle) : false);

      return matchesGroup && matchesKind && matchesLifecycle;
    });
  }, [results, expandedSelectedGroups, filters]);

  const selectedView =
    new URLSearchParams(location.search).get('view') === TRACK_VIEWS.ENTITIES
      ? TRACK_VIEWS.ENTITIES
      : TRACK_VIEWS.CHECKS;

  if (loading) {
    return (
      <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
        <Header title={track?.name ?? id} description={track?.description} />
        <PulseDetailsSkeleton />
      </BreadcrumbEntry>
    );
  }

  if (error) {
    return (
      <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
        <Header title={id} tags={[{ label: 'Back to Tracks', href: '../' }]} />
        <Container>
          <Text>{`Error: ${error.message}`}</Text>
        </Container>
      </BreadcrumbEntry>
    );
  }

  if (!details) {
    return (
      <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
        <EmptyState
          title="Track not found"
          description={`No track with id "${id}" was found.`}
        />
      </BreadcrumbEntry>
    );
  }

  const levelInfo = details.track.levels.map(level => ({
    ordinal: level.ordinal,
    label: getLevelImportanceLabel(level.importance),
  }));

  const filteredDetails = { ...details, results: filteredResults };
  const hasActiveEntityFilter =
    filteredResults.length !== details.results.length;
  const filteredEntityRefs = filteredResults.map(result => result.entityRef);
  const includedFilter = formatEntityFilter(details.track.filter);
  const excludedFilter = formatEntityFilter(details.track.exclude);
  const scopeFilter = excludedFilter
    ? `${includedFilter || 'All entities'}; excluding ${excludedFilter}`
    : includedFilter || 'All entities';

  return (
    <BreadcrumbEntry entry={{ label: breadcrumbLabel, href: breadcrumbHref }}>
      <Header
        title={details.track.name}
        description={details.track.description}
        customActions={
          <TrackActionsButton
            track={details.track}
            onDeleted={() => navigate('..')}
          />
        }
        metadata={[
          {
            label: 'Owner',
            value: (
              <EntityRefLink
                entityRef={details.track.ownerEntityRef}
                underline="none"
              />
            ),
          },
          {
            label: 'Scope Filter',
            value: scopeFilter,
          },
          {
            label: 'Status',
            value: details.track.inactive ? (
              <>
                <RiCircleFill size={9} color="var(--bui-fg-warning)" /> Inactive
              </>
            ) : (
              <>
                <RiCircleFill size={9} color="var(--bui-fg-positive)" /> Active
              </>
            ),
          },
        ]}
      />
      <Container>
        <Flex direction="column" gap="12">
          {savedNotice ? (
            <Alert
              status="success"
              icon
              title={
                savedNotice === 'created' ? 'Track created.' : 'Track updated.'
              }
              customActions={
                <Button
                  variant="tertiary"
                  size="small"
                  iconStart={<RiCloseLine size={12} />}
                  onPress={() => setSavedNotice(null)}
                >
                  Dismiss
                </Button>
              }
            />
          ) : null}
          <FilterBar
            entityOwnerOptions={entityOwnerOptionsWithTeams}
            selectedEntityOwners={selectedGroups}
            onEntityOwnersChange={setSelectedGroups}
            entityKindOptions={entityKindOptions}
            lifecycleOptions={lifecycleOptions}
            value={filters}
            onChange={setFilters}
          />
          <Flex direction="column" gap="6">
            <Grid.Root columns={{ initial: '1', lg: '2' }} gap="4">
              <Grid.Item>
                <CurrentStatusCard details={filteredDetails} />
              </Grid.Item>

              <Grid.Item>
                <TrackHistoryChartCard
                  trackId={id}
                  levels={levelInfo}
                  isFiltered={hasActiveEntitySelection(filters, selectedGroups)}
                />
              </Grid.Item>
            </Grid.Root>

            <Tabs
              selectedKey={selectedView}
              onSelectionChange={key => {
                const nextView =
                  key === TRACK_VIEWS.ENTITIES
                    ? TRACK_VIEWS.ENTITIES
                    : TRACK_VIEWS.CHECKS;
                if (nextView !== selectedView) {
                  navigate(withViewParam(nextView, location));
                }
              }}
            >
              <TabList>
                <Tab id={TRACK_VIEWS.CHECKS}>Checks By Levels</Tab>
                <Tab id={TRACK_VIEWS.ENTITIES}>Entities</Tab>
              </TabList>
              <TabPanel id={TRACK_VIEWS.CHECKS}>
                <ChecksByLevels
                  details={details}
                  entityRefs={
                    hasActiveEntityFilter ? filteredEntityRefs : undefined
                  }
                  selectedGroups={selectedGroups}
                />
              </TabPanel>
              <TabPanel id={TRACK_VIEWS.ENTITIES}>
                <TrackEntitiesTable details={filteredDetails} />
              </TabPanel>
            </Tabs>
          </Flex>
        </Flex>
      </Container>
    </BreadcrumbEntry>
  );
};
